import type { SxProps, Theme } from '@mui/material/styles';
import type { FilterConfig, FilterOption } from '@openx/types/chipFilters.ts';
import type { BuyerOption } from '@openx/types/options.ts';
import { parseMultiValueFilter, serializeMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';

type BuyersMap = Record<string, BuyerOption | undefined>;

const getBuyerDisplayName = (name: string): string => {
  const separatorIndex = name.indexOf(' > ');
  return separatorIndex >= 0 ? name.slice(0, separatorIndex) : name;
};

export const buildBuyerFilterOptions = (buyersMap: BuyersMap): FilterOption[] => {
  const optionsByBuyerId = new Map<string, FilterOption>();

  Object.entries(buyersMap).forEach(([mapKey, buyer]) => {
    if (!buyer?.name) {
      return;
    }

    const buyerId = buyer.buyer_id ?? buyer.id;
    if (optionsByBuyerId.has(buyerId)) {
      return;
    }

    optionsByBuyerId.set(buyerId, {
      displayName: getBuyerDisplayName(buyer.name),
      value: buyer.seat_id ?? mapKey,
    });
  });

  return Array.from(optionsByBuyerId.values()).sort((left, right) =>
    (left.displayName ?? left.value?.toString() ?? '').localeCompare(right.displayName ?? right.value?.toString() ?? '')
  );
};

export const expandBuyerFilterToSeatIds = (
  filterValue: string | undefined,
  buyersMap: BuyersMap
): string | undefined => {
  const selectedSeatIds = parseMultiValueFilter(filterValue);
  if (!selectedSeatIds.length) {
    return undefined;
  }

  const seatsByBuyerId = new Map<string, string[]>();
  const seatToBuyerId = new Map<string, string>();

  Object.entries(buyersMap).forEach(([mapKey, buyer]) => {
    if (!buyer) {
      return;
    }

    const buyerId = buyer.buyer_id ?? buyer.id;
    const seatId = buyer.seat_id ?? mapKey;
    seatToBuyerId.set(seatId, buyerId);

    const existingSeats = seatsByBuyerId.get(buyerId) ?? [];
    if (!existingSeats.includes(seatId)) {
      seatsByBuyerId.set(buyerId, [...existingSeats, seatId]);
    }
  });

  const expandedSeatIds = new Set<string>();
  selectedSeatIds.forEach(seatId => {
    const buyerId = seatToBuyerId.get(seatId);
    const buyerSeats = buyerId ? seatsByBuyerId.get(buyerId) : undefined;

    if (buyerSeats?.length) {
      buyerSeats.forEach(id => {
        expandedSeatIds.add(id);
      });
      return;
    }

    expandedSeatIds.add(seatId);
  });

  const serialized = serializeMultiValueFilter(Array.from(expandedSeatIds));
  return serialized || undefined;
};

export const collapseBuyerFilterDraftValue = (filterValue: string | undefined, buyersMap: BuyersMap): string => {
  const selectedSeatIds = parseMultiValueFilter(filterValue);
  if (!selectedSeatIds.length) {
    return '';
  }

  const seatToBuyerId = new Map<string, string>();
  const representativeSeatByBuyerId = new Map<string, string>();

  Object.entries(buyersMap).forEach(([mapKey, buyer]) => {
    if (!buyer) {
      return;
    }

    const buyerId = buyer.buyer_id ?? buyer.id;
    const seatId = buyer.seat_id ?? mapKey;
    seatToBuyerId.set(seatId, buyerId);

    if (!representativeSeatByBuyerId.has(buyerId)) {
      representativeSeatByBuyerId.set(buyerId, seatId);
    }
  });

  const representativeSeats = new Set<string>();
  selectedSeatIds.forEach(seatId => {
    const buyerId = seatToBuyerId.get(seatId);
    if (buyerId) {
      const representativeSeat = representativeSeatByBuyerId.get(buyerId);
      if (representativeSeat) {
        representativeSeats.add(representativeSeat);
      }
      return;
    }

    representativeSeats.add(seatId);
  });

  return serializeMultiValueFilter(Array.from(representativeSeats));
};

export const collapseBuyerRememberedValues = (rememberedValues: string[], buyersMap: BuyersMap): string[] =>
  parseMultiValueFilter(collapseBuyerFilterDraftValue(serializeMultiValueFilter(rememberedValues), buyersMap));

export const areBuyerFilterValuesEqual = (
  appliedValue: string | undefined,
  draftValue: string | undefined,
  buyersMap: BuyersMap
): boolean => {
  const appliedExpanded = expandBuyerFilterToSeatIds(appliedValue, buyersMap) ?? '';
  const draftExpanded = expandBuyerFilterToSeatIds(draftValue, buyersMap) ?? '';
  return appliedExpanded === draftExpanded;
};

export const getVisibleFilterOptions = (options: FilterOption[]): FilterOption[] =>
  options.filter(option => !option.hidden && (!!option.displayName || !!option.value));

export const getFilterOptionLabel = (option: string | FilterOption): string => {
  if (typeof option === 'string') {
    return option;
  }

  return option.displayName || option.value?.toString() || option.toString();
};

export const findFilterOptionByValue = (
  filterConfig: FilterConfig,
  value: string | null | undefined
): FilterOption | null => {
  if (!value) {
    return null;
  }

  return (
    filterConfig.options?.find(option => String(option.value) === value || String(option.displayName) === value) ?? {
      value,
    }
  );
};

export const getSelectedFilterOption = (
  filterConfig: FilterConfig,
  draftValue: string,
  { allowTextValue = false }: { allowTextValue?: boolean } = {}
): FilterOption | null => {
  if (!draftValue) {
    return null;
  }

  if (allowTextValue) {
    return { value: draftValue };
  }

  return findFilterOptionByValue(filterConfig, draftValue);
};

export const getOptionalFilterValue = (value: string | undefined): string | undefined =>
  value ? String(value) : undefined;

type GetMoreFiltersSelectableOptionSxParams = {
  isChecked: boolean;
  minHeight: number;
  labelStyles?: Record<string, unknown>;
};

export const getMoreFiltersSelectableOptionSx = ({
  isChecked,
  minHeight,
  labelStyles = {},
}: GetMoreFiltersSelectableOptionSxParams): SxProps<Theme> => ({
  width: '100%',
  minHeight,
  m: 0,
  px: 1,
  py: 0,
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme => (isChecked ? theme.palette.primary.light : 'transparent'),
  '.MuiFormControlLabel-labelPlacementEnd': {
    marginLeft: 0,
  },
  '.MuiCheckbox-root': {
    p: 0,
    mr: 1,
  },
  '.MuiFormControlLabel-label': labelStyles,
  '&:hover': {
    backgroundColor: theme => (isChecked ? theme.palette.primary.light : theme.palette.action.hover),
  },
});
